import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Api from '../../api/global.service';
import env from '../../environment';
import useOrquestadorSse from '../../hooks/useOrquestadorSse';
import { getUsaComiteEvaluacion } from '../content/coop-config.jsx';

const OPCIONES_AGRUPACION = [
  { value: 'productor', label: 'Productor' },
  { value: 'localidad', label: 'Localidad' },
  { value: 'municipio', label: 'Municipio' },
  { value: 'region',    label: 'Región' },
];

const PASOS = [
  { num: 1, key: 'paso_1_fichas', label: 'Generando PDFs SIC',              icon: 'fa-file-text-o' },
  { num: 2, key: 'paso_2_comite', label: 'Generando PDFs evaluación',       icon: 'fa-gavel'        },
  { num: 3, key: 'paso_3_zip',    label: 'Acomodando los archivos en carpetas', icon: 'fa-folder-open-o' },
  { num: 4, key: null,            label: 'Generando ZIP',                   icon: 'fa-file-archive-o' },
];

const MENSAJE_ANTES_DESCARGA_CON_COMITE =
  'La descarga completa de fichas y evaluaciones puede tomar unos minutos. Da click en Descargar para continuar.';
const MENSAJE_ANTES_DESCARGA_SIN_COMITE = 'La descarga completa de fichas puede tomar unos minutos. Da click en Descargar para continuar.';
const MENSAJE_DURANTE_GENERACION =
  'La generación de fichas y reportes está en curso. Puedes esperar a que la descarga se complete o regresar a esta ventana en unos minutos para obtener el archivo ZIP.';

// Segundos promedio por unidad (ver sección "Rendimiento" del módulo) — se
// usan como peso para que el % general refleje el tiempo real que toma cada
// paso, no solo "cuántos de N pasos van". Una ficha tarda ~5x más que una
// evaluación de comité, así que pesarlos igual haría que el % avanzara a
// saltos irreales en cooperativas con muchas evaluaciones y pocas fichas.
const PESO_FICHA = 1.45;
const PESO_COMITE = 0.50;
// El paso de empacar el ZIP no reporta avance incremental (solo
// 'procesando'/'listo', ver paso_3_zip) — se le asigna un peso estimado como
// fracción del trabajo de generación, en vez de un tiempo fijo, para que
// escale con el tamaño de la cooperativa igual que los otros dos pasos.
const PESO_ZIP_FACTOR = 0.08;

/**
 * % de avance combinando los 3 pasos reales del wizard, ponderado por tiempo
 * estimado (no por "1 de 3 pasos"). Dos casos especiales:
 * - Un paso 'detenido' (registros rotos, regla #24) cuenta como resuelto
 *   para este cálculo, porque el wizard avanza igual (regla #33) — si no, la
 *   barra se quedaría pegada por debajo de 100% aunque el stepper ya avanzó.
 * - El paso del ZIP no tiene progreso granular: mientras está 'procesando'
 *   se le acredita la mitad de su peso (para que la barra no se congele en
 *   ese tramo) y el resto al llegar a 'listo'.
 */
const calcularProgresoGeneral = (estado, listo) => {
  if (listo) return { porcentaje: 100, zipEnProgreso: false };

  const paso1 = estado?.paso_1_fichas;
  const paso2 = estado?.paso_2_comite;
  const paso3 = estado?.paso_3_zip;

  const totalFichas = paso1?.total ?? 0;
  const totalComite = paso2?.total ?? 0;

  const procesadasFichas = paso1?.estado === 'detenido' ? totalFichas : (paso1?.procesadas ?? 0);
  const procesadasComite = paso2?.estado === 'detenido' ? totalComite : (paso2?.procesadas ?? 0);

  const trabajoFichas = totalFichas * PESO_FICHA;
  const trabajoComite = totalComite * PESO_COMITE;
  const trabajoZip = (trabajoFichas + trabajoComite) * PESO_ZIP_FACTOR;
  const trabajoTotal = trabajoFichas + trabajoComite + trabajoZip;

  if (trabajoTotal <= 0) return { porcentaje: 0, zipEnProgreso: false };

  const zipProcesando = paso3?.estado === 'procesando';
  const hechoZip = paso3?.estado === 'listo' ? trabajoZip : (zipProcesando ? trabajoZip * 0.5 : 0);
  const hechoTotal = procesadasFichas * PESO_FICHA + procesadasComite * PESO_COMITE + hechoZip;

  // Tope en 99 mientras no esté 'listo' — el 100% real lo marca `listo`
  // arriba, para no mostrar "completo" un instante antes de que lo esté.
  const porcentaje = Math.min(99, Math.round((hechoTotal / trabajoTotal) * 100));
  return { porcentaje, zipEnProgreso: zipProcesando };
};

const AvisoInfo = ({ children }) => (
  <div className="alert alert-warning d-flex align-items-start mb-3" style={{ fontSize: '0.95em', borderRadius: '24px' }}>
    <i className="fa fa-info-circle mr-2 mt-1"></i>
    <span>{children}</span>
  </div>
);

const StatRow = ({ icon, label, value }) => (
  <div
    className="d-flex justify-content-between align-items-center py-2 px-3"
    style={{ borderBottom: '1px solid #f0f0f0' }}>
    <span className="text-muted" style={{ fontSize: '1.05em' }}>
      <i className={`fa ${icon} mr-2`}></i>
      {label}
    </span>
    <strong style={{ color: '#333' }}>{value}</strong>
  </div>
);

const Spinner = ({ sm }) => (
  <div
    className={`spinner-border ${sm ? 'spinner-border-sm' : ''} text-warning`}
    role="status"
  />
);

const ModalDescargaFichas = ({ show, ciclo, totalProductores, onClose }) => {
  const usaComiteEvaluacion = getUsaComiteEvaluacion() === 1;
  // Step numbers (paso.num) must stay aligned with the backend's paso_actual,
  // so the comité step is filtered out of the display list without renumbering.
  const pasosVisibles = usaComiteEvaluacion ? PASOS : PASOS.filter(p => p.key !== 'paso_2_comite');
  const mensajeAntesDescarga = usaComiteEvaluacion ? MENSAJE_ANTES_DESCARGA_CON_COMITE : MENSAJE_ANTES_DESCARGA_SIN_COMITE;
  const [errorMsg, setErrorMsg] = useState(null);
  const [agrupacion, setAgrupacion] = useState('productor');
  const [enviando, setEnviando] = useState(false);
  // Controla qué pantalla se muestra — independiente de si ya existe una fila
  // orquestador en BD. Por defecto siempre arranca en la pantalla inicial
  // (conteos + selector de agrupación), incluso si el wizard de una sesión
  // anterior ya quedó 'completo' — así el usuario puede pedir el mismo
  // contenido agrupado por otro criterio. Solo se salta directo al stepper
  // si detecta un orquestador activo en curso (ver efecto abajo), o cuando
  // el usuario mismo presiona "Descargar".
  const [mostrarPasos, setMostrarPasos] = useState(false);
  // El POST responde en cuanto se encola, pero el SSE tarda unos segundos en
  // reflejar el orquestador recién creado — sin esta bandera, entre que
  // `enviando` vuelve a false y `orquestador` aparece en `estado`, el botón
  // quedaba habilitado y se podía volver a presionar "Descargar".
  const [esperandoOrquestador, setEsperandoOrquestador] = useState(false);
  // Evita que clicks repetidos en "Descargar ZIP" mientras el fetch/blob del
  // archivo (varios MB, puede tardar unos segundos) sigue en curso disparen
  // varias descargas concurrentes del mismo archivo.
  const [descargando, setDescargando] = useState(false);
  // Porcentaje (0-100) de bytes recibidos del ZIP mientras se descarga, o
  // null si el servidor no mandó Content-Length (no hay forma de medir).
  const [descargaProgreso, setDescargaProgreso] = useState(null);

  // Snapshot inicial (con totales absolutos del ciclo) + actualizaciones vía
  // SSE unificado. El backend ya calcula los totales sobre tablas crudas
  // (no la vista de comité, que es pesada), así que este endpoint es rápido.
  const { estado, refrescar } = useOrquestadorSse({
    ciclo,
    active: show && !!ciclo?.id,
  });

  const orquestadorSse = estado?.orquestador || null;

  // En cuanto el SSE confirma el orquestador, ya no hace falta seguir
  // bloqueando el botón por esta bandera — el propio orquestador toma el control.
  useEffect(() => {
    if (orquestadorSse) setEsperandoOrquestador(false);
  }, [orquestadorSse]);

  // Si al abrir el modal (o durante el polling) el orquestador ya está
  // activo y sano, saltar directo al stepper — no tiene sentido mostrar el
  // selector de agrupación mientras un proceso sigue corriendo en segundo
  // plano para la agrupación que se eligió al iniciarlo.
  useEffect(() => {
    if (!orquestadorSse) return;
    const activo = ['iniciando', 'procesando'].includes(orquestadorSse.estado) && !orquestadorSse.atascado;
    if (activo) setMostrarPasos(true);
  }, [orquestadorSse]);

  const handleDescargar = () => {
    setMostrarPasos(true);
    setEnviando(true);
    setEsperandoOrquestador(true);
    setErrorMsg(null);
    Api.create('admin/sic/fichas/iniciar-descarga', {
      id_ciclo: ciclo.id,
      agrupacion,
    })
      .catch(err => {
        if (err.response?.status === 409) {
          // Ya hay uno en curso — no es error, el SSE lo reflejará
        } else {
          setErrorMsg('No se pudo iniciar la descarga.');
          setEsperandoOrquestador(false);
        }
      })
      .finally(() => {
        setEnviando(false);
        // Reactivar el polling (o forzar un refresco) SOLO después de que el
        // POST haya resuelto — iniciar-descarga ya actualizó
        // agrupacion_orquestador y corrió resync() de forma síncrona antes
        // de responder. Llamar a refrescar() ANTES/EN PARALELO con el POST
        // (como antes) dejaba una ventana real: el GET de orquestador-estado
        // podía llegar primero y disparar su propia auto-reparación viendo
        // TODAVÍA el orquestador viejo en 'completo' — devolviendo ese
        // estado (agrupación anterior, ZIP viejo ya 'listo') antes de que la
        // nueva agrupación se hubiera aplicado en BD.
        refrescar();
      });
  };

  const descargarZip = async () => {
    if (descargando) return;

    const zipUuid = estado?.paso_3_zip?.uuid;
    if (!zipUuid) return;

    // El nombre del archivo debe reflejar la agrupación con la que el
    // servidor REALMENTE generó este ZIP (estado.orquestador.agrupacion_orquestador),
    // no el radio button local — que puede haber quedado desactualizado si
    // esta descarga adoptó un orquestador ya en curso para otra agrupación
    // (iniciar-descarga responde 409 y el frontend no cambia `agrupacion`).
    const agrupacionReal = estado?.orquestador?.agrupacion_orquestador || agrupacion;

    setDescargando(true);
    setDescargaProgreso(0);
    try {
      const token = localStorage.getItem(`${env.appName}-token`);
      const res = await fetch(`${env.apiURL}/admin/sic/fichas/descargar-zip/${zipUuid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Lee el body por chunks (en vez de res.blob() directo) para poder
      // reportar el % de bytes recibidos — útil en ZIPs pesados (varios MB).
      // Content-Length viene del backend (response()->download() lo manda);
      // sin él (o sin soporte de streams en el navegador) no hay forma real
      // de medir progreso, así que se cae de vuelta a res.blob() sin barra.
      const totalBytes = parseInt(res.headers.get('Content-Length') || '', 10);
      let blob;
      if (totalBytes > 0 && res.body?.getReader) {
        const reader = res.body.getReader();
        const chunks = [];
        let recibidos = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          recibidos += value.length;
          setDescargaProgreso(Math.min(99, Math.round((recibidos / totalBytes) * 100)));
        }
        blob = new Blob(chunks);
      } else {
        blob = await res.blob();
      }
      setDescargaProgreso(100);

      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `fichas_ciclo_${ciclo.nombre}_${agrupacionReal}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Descarga completa',
        text: 'El archivo ZIP se descargó correctamente.',
        confirmButtonText: 'Continuar',
      }).then(() => handleClose());
    } catch (_) {
      setErrorMsg('No se pudo descargar el ZIP.');
    } finally {
      setDescargando(false);
      setDescargaProgreso(null);
    }
  };

  const handleClose = () => {
    setErrorMsg(null);
    setAgrupacion('productor');
    setEsperandoOrquestador(false);
    setMostrarPasos(false);
    onClose();
  };

  if (!show) return null;

  const orquestador  = estado?.orquestador || null;
  const pasoActual   = orquestador?.paso_actual ?? 0;
  // `esperandoOrquestador` sigue en true hasta que llega una confirmación
  // fresca del orquestador tras el POST (ver handleDescargar/refrescar). Sin
  // este guard, un `estado` viejo en memoria (orquestador 'completo' de una
  // descarga previa con otra agrupación) hacía que `listo` fuera `true` un
  // instante antes de que la respuesta real llegara — pintando los 4 pasos
  // en verde de golpe (estadoPaso() corta directo a 'completado' si `listo`)
  // y habilitando "Descargar ZIP" con el archivo de la agrupación anterior.
  const listo        = !esperandoOrquestador && orquestador?.estado === 'completo';
  const zipUuid      = estado?.paso_3_zip?.uuid;
  const zipEstado    = estado?.paso_3_zip?.estado;
  const progresoGeneral = calcularProgresoGeneral(estado, listo);

  // Una sola barra para todo el ciclo de vida visible del modal: mientras se
  // genera (pasos 1-4) sigue a `progresoGeneral` hasta llegar a 100; al
  // presionar "Descargar ZIP", `descargarZip()` resetea `descargaProgreso` a
  // 0 y la misma barra pasa a seguir los bytes recibidos del archivo — nunca
  // se muestran dos barras compitiendo por la misma información.
  const barraGeneralPorcentaje = descargando ? (descargaProgreso ?? 0) : progresoGeneral.porcentaje;
  const barraGeneralAnimada    = !descargando && progresoGeneral.zipEnProgreso;
  const barraGeneralLabel      = descargando
    ? 'Descargando ZIP'
    : (listo ? 'Generación completa' : 'Avance general');

  // Independiente de qué pantalla se esté viendo (mostrarPasos) — si el
  // orquestador está realmente activo en el backend, el botón "Descargar"
  // debe reflejarlo. Antes solo se consideraba mientras mostrarPasos era
  // true, así que si el usuario volvía a la pantalla inicial (p. ej. con
  // "Regresar") justo mientras un proceso seguía corriendo en segundo plano,
  // el botón quedaba habilitado incorrectamente por hasta ~2s (el tiempo
  // que tarda el siguiente poll del SSE en forzar el auto-salto al stepper).
  const procesandoActivo = !!orquestador
    && ['iniciando', 'procesando'].includes(orquestador.estado)
    && !orquestador.atascado;

  // Cálculo de estado por paso para colorear
  const estadoPaso = (pasoNum) => {
    if (listo) return 'completado';

    // El wizard es secuencial (nunca corre 2 pasos en paralelo — ver
    // WizardOrquestadorResyncService::resync()), así que fichas/comité no
    // se marcan como su "turno" hasta que el paso anterior termina. Pero si
    // ya no quedan pendientes reales para ese paso (p. ej. comité ya se
    // generó por completo en una corrida previa), mostrarlo en verde de una
    // vez en vez de dejarlo gris esperando su turno — hace más transparente
    // el "Avance general" (que ya contaba ese trabajo, aunque el stepper no
    // lo reflejara). Solo aplica a pasos 1/2, los únicos con `pendientes`
    // en el payload — paso 3 (zip) no tiene ese dato.
    if (pasoNum <= 2) {
      const subAdelantado = [estado?.paso_1_fichas, estado?.paso_2_comite][pasoNum - 1];
      if (subAdelantado && subAdelantado.total > 0 && subAdelantado.pendientes === 0) {
        return 'completado';
      }
    }

    if (pasoNum < pasoActual)  return 'completado';
    if (pasoNum === pasoActual) {
      const sub = [estado?.paso_1_fichas, estado?.paso_2_comite, estado?.paso_3_zip][pasoNum - 1];
      if (sub?.estado === 'detenido' || sub?.estado === 'error') return 'error';
      if (sub?.estado === 'procesando' || sub?.estado === 'iniciando') return 'en_curso';
      if (sub?.estado === 'completo' || sub?.estado === 'listo') return 'completado';
      return 'pendiente';
    }
    return 'pendiente';
  };

  return (
    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 540 }}>
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header d-flex align-items-center">
            <div className="d-flex align-items-center flex-grow-1">
              {/* Flecha "regresar" — solo con el wizard ya completado, para
                  volver a la pantalla inicial y descargar por otra agrupación
                  sin tener que cerrar y reabrir el modal. */}
              {mostrarPasos && listo && (
                <button
                  onClick={() => {
                    // Limpiar banderas de "en vuelo" de la descarga que ya
                    // terminó — el botón "Descargar" de la pantalla inicial
                    // no se renderiza mientras se ve el stepper completado,
                    // así que si quedaron en true no se notaba hasta ahora.
                    setEnviando(false);
                    setEsperandoOrquestador(false);
                    setMostrarPasos(false);
                  }}
                  type="button"
                  className="btn btn-link p-0 mr-2"
                  title="Regresar"
                  style={{ color: '#333', fontSize: '1.2em', lineHeight: 1 }}>
                  <i className="fa fa-arrow-left"></i>
                </button>
              )}
              <h5 className="modal-title mb-0">
                <i className="fa fa-file-archive-o mr-2"></i>
                {usaComiteEvaluacion ? 'Descargar fichas SIC y comité' : 'Descargar fichas SIC'}
                {ciclo?.nombre && (
                  <span className="text-muted ml-2" style={{ fontSize: '0.85em', fontWeight: 400 }}>
                    — Ciclo {ciclo.nombre}
                  </span>
                )}
              </h5>
            </div>
            <button onClick={handleClose} type="button" className="close">
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">
            {errorMsg && (
              <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3">
                <span><i className="fa fa-exclamation-circle mr-2"></i>{errorMsg}</span>
                <button
                  className="btn btn-sm btn-outline-secondary btn-sirio ml-2"
                  style={{ borderRadius: '20px', whiteSpace: 'nowrap' }}
                  onClick={() => { setErrorMsg(null); }}>
                  <i className="fa fa-times mr-1"></i> Cerrar
                </button>
              </div>
            )}

            {/* ── Conteos (solo en la pantalla inicial) ── */}
            {!mostrarPasos && (
              <div className="border rounded mb-3" style={{ overflow: 'hidden' }}>
                <StatRow icon="fa-users"       label="Productores en padrón"   value={totalProductores ?? '—'} />
                <StatRow icon="fa-file-text-o" label="Fichas SIC en el ciclo"  value={estado?.total_fichas ?? '—'} />
                {usaComiteEvaluacion && (
                  <StatRow icon="fa-gavel"     label="Evaluaciones de comité en el ciclo" value={estado?.total_evaluaciones ?? '—'} />
                )}
              </div>
            )}

            {/* ── Selector de agrupación (solo en la pantalla inicial) ── */}
            {!mostrarPasos && (
              <>
                <p className="mb-1 text-muted" style={{ fontSize: '1em', fontWeight: 600 }}>
                  Agrupar archivos por:
                </p>
                <div className="d-flex flex-wrap mb-3" style={{ gap: '8px' }}>
                  {OPCIONES_AGRUPACION.map(op => (
                    <label
                      key={op.value}
                      className="d-flex align-items-center"
                      style={{ cursor: 'pointer', gap: '4px', fontSize: '1.05em' }}>
                      <input
                        type="radio"
                        name="agrupacion"
                        value={op.value}
                        checked={agrupacion === op.value}
                        onChange={() => setAgrupacion(op.value)}
                        style={{ accentColor: '#789D16' }}
                      />
                      {op.label}
                    </label>
                  ))}
                </div>
              </>
            )}

            {/* ── Aviso antes de iniciar la descarga ── */}
            {!mostrarPasos && <AvisoInfo>{mensajeAntesDescarga}</AvisoInfo>}

            {/* ── Stepper de 4 pasos (solo después de presionar Descargar) ── */}
            {mostrarPasos && (
              <div className="border rounded p-3" style={{ backgroundColor: '#fafafa' }}>
              {pasosVisibles.map((paso, idx) => {
                const stepEstado = estadoPaso(paso.num);
                const sub = paso.key ? estado?.[paso.key] : null;
                const showSpinner = stepEstado === 'en_curso';

                let circleBg, circleColor, textColor, iconClass;
                if (stepEstado === 'completado') {
                  circleBg    = '#789D16';
                  circleColor = '#fff';
                  textColor   = '#789D16';
                  iconClass   = 'fa-check';
                } else if (stepEstado === 'en_curso') {
                  circleBg    = '#f0f0f0';
                  circleColor = '#666';
                  textColor   = '#333';
                  iconClass   = paso.icon;
                } else if (stepEstado === 'error') {
                  circleBg    = '#d9534f';
                  circleColor = '#fff';
                  textColor   = '#d9534f';
                  iconClass   = 'fa-exclamation-triangle';
                } else {
                  circleBg    = '#f0f0f0';
                  circleColor = '#999';
                  textColor   = '#999';
                  iconClass   = paso.icon;
                }

                const porcentaje = sub?.total ? Math.min(100, Math.round((sub.procesadas / sub.total) * 100)) : null;
                const progreso = sub?.total
                  ? ` ${sub.procesadas} de ${sub.total}${porcentaje !== null ? ` (${porcentaje}%)` : ''}`
                  : (sub?.total_fichas ? ` ${sub.total_fichas} reportes` : '');

                return (
                  <div
                    key={paso.num}
                    className={`d-flex align-items-center ${idx < pasosVisibles.length - 1 ? 'mb-3' : ''}`}>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 32, height: 32,
                        backgroundColor: circleBg,
                        color: circleColor,
                        flexShrink: 0,
                        fontSize: '0.9em',
                        fontWeight: 600,
                        boxShadow: stepEstado === 'en_curso' ? '0 0 0 3px rgba(255,193,7,0.18)' : 'none',
                      }}>
                      {showSpinner
                        ? <Spinner sm />
                        : stepEstado === 'completado'
                          ? <i className="fa fa-check"></i>
                          : idx + 1}
                    </div>
                    <div className="ml-3 flex-grow-1">
                      <div style={{ fontSize: '1em', fontWeight: 600, color: textColor }}>
                        {idx + 1}. {paso.label}
                      </div>
                      {(showSpinner || stepEstado === 'completado') && progreso && (
                        <div className="text-muted" style={{ fontSize: '0.85em' }}>
                          {progreso}
                        </div>
                      )}
                      {stepEstado === 'error' && sub?.error_message && (
                        <div style={{ fontSize: '0.85em', color: '#d9534f' }}>
                          {sub.error_message}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            {/* ── Aviso durante la generación (pasos 1 a 3), antes de los botones ── */}
            {mostrarPasos && !listo && (
              <div style={{ width: '100%' }}>
                <AvisoInfo>{MENSAJE_DURANTE_GENERACION}</AvisoInfo>
              </div>
            )}

            {/* ── Barra de avance única: generación (0-100) hasta `listo`, luego
                se resetea a 0 al presionar "Descargar ZIP" y sigue los bytes
                recibidos de la descarga ── */}
            {mostrarPasos && (
              <div style={{ width: '100%' }} className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 600 }}>
                    {barraGeneralLabel}
                  </span>
                  <strong style={{ color: '#789D16', fontSize: '0.85em' }}>{barraGeneralPorcentaje}%</strong>
                </div>
                <div className="progress" style={{ height: 8, borderRadius: 10 }}>
                  <div
                    className={`progress-bar ${barraGeneralAnimada ? 'progress-bar-striped progress-bar-animated' : ''}`}
                    role="progressbar"
                    style={{ width: `${barraGeneralPorcentaje}%`, backgroundColor: '#789D16' }}
                    aria-valuenow={barraGeneralPorcentaje}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleClose}
              type="button"
              className="btn btn-outline-secondary btn-sirio"
              style={{ borderRadius: '20px' }}>
              <i className="fa fa-times mr-1"></i> Cerrar
            </button>

            {/* Estado final (viendo el stepper): ZIP listo → Descargar */}
            {mostrarPasos && listo && zipUuid && zipEstado === 'listo' && (
              <button
                onClick={descargarZip}
                disabled={descargando}
                type="button"
                className="btn"
                style={{
                  borderRadius: '20px',
                  backgroundColor: '#789D16',
                  color: '#fff',
                  border: 'none',
                  opacity: descargando ? 0.6 : 1,
                }}>
                {descargando
                  ? <><Spinner sm /> Descargando{descargaProgreso !== null ? ` ${descargaProgreso}%` : '...'}</>
                  : <><i className="fa fa-download mr-1"></i> Descargar ZIP</>}
              </button>
            )}

            {/* Pantalla inicial, o viendo el stepper sin terminar → botón único
                amarillo "Descargar" / "Procesando..." / "Reanudar". En la
                pantalla inicial siempre se puede presionar (incluso si ya hay
                un orquestador completo de una agrupación distinta — permite
                pedir el mismo contenido agrupado de otra forma). */}
            {(!mostrarPasos || !listo) && (
              <button
                onClick={handleDescargar}
                disabled={enviando || esperandoOrquestador || procesandoActivo}
                type="button"
                className="btn"
                style={{
                  borderRadius: '20px',
                  backgroundColor: '#f0ad4e',
                  color: '#fff',
                  border: 'none',
                  opacity: (enviando || esperandoOrquestador || procesandoActivo) ? 0.6 : 1,
                  minWidth: 160,
                }}>
                {(enviando || esperandoOrquestador)
                  ? <><Spinner sm /> Enviando...</>
                  : procesandoActivo
                    ? <><Spinner sm /> Procesando...</>
                    : (orquestador?.atascado)
                      ? <><i className="fa fa-refresh mr-1"></i> Reanudar</>
                      : <><i className="fa fa-download mr-1"></i> Descargar</>}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ModalDescargaFichas;
